import * as React from 'react';
import type { BevelRenderSettings } from '../utils/getBevelDepth';

const CENTER = 128 / 255;
const BOX = Array(9)
  .fill(1 / 9)
  .join(' ');
// Native one-pixel Chisel Hard controls show that contour antialiasing
// averages surface normals before lighting. Keep this calibrated footprint out
// of saved JSON. Other techniques and contours retain their existing surfaces.
export function getNormalBevelSampling(
  depth: BevelRenderSettings,
  pixelRatio = 1
) {
  if (
    depth.technique !== 'chisel-hard' ||
    depth.documentSize !== 1 ||
    depth.softness !== 0 ||
    !['linear', 'ring'].includes(depth.contour) ||
    depth.contourRange > 25 ||
    depth.glossContour !== 'linear' ||
    depth.highlightBlendMode !== 'color-dodge' ||
    depth.shadowBlendMode !== 'linear-burn'
  )
    return null;
  const density = depth.renderScale * pixelRatio;
  const radius = Math.max(0, Math.ceil((3 * density - 1) / 2));
  const order = 2 * radius + 1;
  // Keep the normal filter inside the same supported convolution budget.
  if (order * order > 256) return null;
  // Integrate the calibrated document-pixel box over physical pixel cells.
  const weights = Array.from({ length: order }, (_, index) => {
    const center = index - radius;
    return (
      Math.max(
        0,
        Math.min(center + 0.5, 1.5 * density) -
          Math.max(center - 0.5, -1.5 * density)
      ) /
      (3 * density)
    );
  });
  const kernel =
    density === 1
      ? BOX
      : weights.flatMap((y) => weights.map((x) => x * y)).join(' ');
  const ring = depth.contour === 'ring';
  return {
    kernel,
    order,
    samplingScale: density,
    // The compressed Ring profile needs a steeper normal response. These
    // renderer constants are calibrated against independent native controls;
    // authored Depth, Size, Range, light and blend intensities stay unchanged.
    surfaceScale: ring
      ? depth.surfaceScale * (39 / 5.94) * (15 / depth.contourRange)
      : depth.surfaceScale * (10.85 / 6.05) * (22 / depth.contourRange),
    offsetX: (ring ? 0.13 : 0.43) * density,
    offsetY: (ring ? 0.17 : 0.39) * density,
  };
}

export function normalLightMatrix(azimuth: number, altitude: number) {
  const az = (azimuth * Math.PI) / 180;
  const el = (altitude * Math.PI) / 180;
  const x = Math.cos(az) * Math.cos(el);
  const y = Math.sin(az) * Math.cos(el);
  const z = Math.sin(el);
  const row = `${2 * x} ${2 * y} ${z} 0 ${-2 * CENTER * (x + y)}`;
  return `${row} ${row} ${row} 0 0 0 0 1`;
}

export function NormalBevelLighting({
  surfaceScale,
  azimuth,
  altitude,
  offsetX = 0.43,
  offsetY = 0.39,
  samplingScale = 1,
}: {
  readonly surfaceScale: number;
  readonly azimuth: number;
  readonly altitude: number;
  readonly offsetX?: number;
  readonly offsetY?: number;
  readonly samplingScale?: number;
}) {
  const dx = offsetX;
  const dy = offsetY;
  // feConvolveMatrix reverses the kernel; target 0 samples the positive
  // neighbours. Integer taps avoid Chrome's rounded fractional feOffset.
  const order = Math.ceil(samplingScale) + 1;
  // Scale the original two taps' separation, not just their centroid. Sampling
  // neighbouring pixels at every density would keep one part of the rim fixed.
  const weight = (position: number, offset: number) => {
    const fraction = offset / samplingScale;
    return (
      (position === 0 ? 1 - fraction : 0) +
      fraction * Math.max(0, 1 - Math.abs(position - samplingScale))
    );
  };
  const kernel = (
    samplingScale === 1
      ? [dx * dy, (1 - dx) * dy, dx * (1 - dy), (1 - dx) * (1 - dy)]
      : Array.from({ length: order }, (_, y) =>
          Array.from(
            { length: order },
            (_, x) => weight(order - 1 - x, dx) * weight(order - 1 - y, dy)
          )
        ).flat()
  ).join(' ');

  return (
    <>
      {(
        [
          [0, 0, 'xp'],
          [180, 0, 'xm'],
          [90, 0, 'yp'],
          [270, 0, 'ym'],
          [0, 90, 'zp'],
        ] as const
      ).map(([angle, elevation, name]) => (
        <feDiffuseLighting
          key={name}
          in="surface"
          surfaceScale={surfaceScale}
          lightingColor="white"
          result={name}
        >
          <feDistantLight azimuth={angle} elevation={elevation} />
        </feDiffuseLighting>
      ))}
      {(['x', 'y'] as const).map((axis) => (
        <React.Fragment key={axis}>
          {/* Opposing normal components never overlap. Their product supplies
          opaque alpha without changing signed RGB in arithmetic compositing. */}
          <feComposite
            in={`${axis}p`}
            in2={`${axis}m`}
            operator="arithmetic"
            k1={1 - CENTER}
            k2={0.5}
            k3={-0.5}
            k4={CENTER}
            result={`${axis}signed`}
          />
          <feColorMatrix
            in={`${axis}signed`}
            values={
              axis === 'x'
                ? '1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0 1'
                : '0 0 0 0 0  1 0 0 0 0  0 0 0 0 0  0 0 0 0 1'
            }
            result={`${axis}channel`}
          />
        </React.Fragment>
      ))}
      <feColorMatrix
        in="zp"
        values="0 0 0 0 0  0 0 0 0 0  1 0 0 0 0  0 0 0 0 1"
        result="zchannel"
      />
      <feComposite
        in="xchannel"
        in2="ychannel"
        operator="arithmetic"
        k2={1}
        k3={1}
        result="normalXY"
      />
      <feComposite
        in="normalXY"
        in2="zchannel"
        operator="arithmetic"
        k2={1}
        k3={1}
        result="packedNormal"
      />
      <feConvolveMatrix
        in="packedNormal"
        order={`${order} ${order}`}
        targetX={0}
        targetY={0}
        kernelMatrix={kernel}
        divisor={1}
        edgeMode="none"
        result="averagedNormal"
      />
      <feColorMatrix
        in="averagedNormal"
        values={normalLightMatrix(azimuth, altitude)}
        result="lighting"
        data-normal-light="true"
      />
    </>
  );
}
